import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Users, Plus, X, CheckCircle2, XCircle, IndianRupee, CalendarDays, RefreshCw } from 'lucide-react';
import { formatDate } from '../../lib/format';
import EmployeePicker from '../../components/EmployeePicker';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_COLORS = {
  Submitted: 'bg-slate-50 text-slate-700 border-slate-200',
  Joined: 'bg-blue-50 text-blue-700 border-blue-200',
  'Probation Cleared': 'bg-amber-50 text-amber-700 border-amber-200',
  Paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function Referrals() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [linkModal, setLinkModal] = useState(null);
  const [form, setForm] = useState({ referrer_employee_id: '', referee_name: '', referee_email: '', referee_phone: '', referee_position: '', referee_department: '', notes: '' });
  const [linkForm, setLinkForm] = useState({ referee_employee_id: '', referee_gross_salary: '' });

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const isHRAdmin = role === 'super_admin' || role === 'admin' || dept === 'HR' || dept === 'MD' || role === 'hr' || role === 'md';

  const load = async () => {
    try {
      const d = await apiGet('/hr/benefits/referrals');
      setReferrals(d.referrals || []);
    } catch (err) { toast.error('Failed to load referrals'); }
  };
  useEffect(() => { load(); apiGet('/hr/benefits/policy').then(setPolicy).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referrer_employee_id || !form.referee_name.trim()) { toast.error('Referrer and referee name required'); return; }
    try {
      await apiPost('/hr/benefits/referrals', form);
      toast.success('Referral submitted');
      setShowForm(false);
      setForm({ referrer_employee_id: '', referee_name: '', referee_email: '', referee_phone: '', referee_position: '', referee_department: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const link = async (e) => {
    e.preventDefault();
    if (!linkForm.referee_employee_id || !linkForm.referee_gross_salary) { toast.error('Employee and gross salary required'); return; }
    try {
      await apiPut(`/hr/benefits/referrals/${linkModal.id}/link`, {
        referee_employee_id: linkForm.referee_employee_id,
        referee_gross_salary: parseFloat(linkForm.referee_gross_salary),
      });
      toast.success(`Linked — bonus will trigger after 6-month probation`);
      setLinkModal(null);
      setLinkForm({ referee_employee_id: '', referee_gross_salary: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to link'); }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this referral?')) return;
    try { await apiPut(`/hr/benefits/referrals/${id}/reject`, {}); load(); }
    catch (err) { toast.error('Failed'); }
  };

  const markPaid = async (id) => {
    try {
      await apiPut(`/hr/benefits/referrals/${id}/mark-paid`, {});
      toast.success('Marked as paid');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const runProbationCron = async () => {
    try {
      const r = await apiPost('/hr/benefits/referrals/auto-clear-probations', {});
      toast.success(`Probation scan: ${r.flipped} cleared / ${r.scanned} scanned`);
      load();
    } catch (err) { toast.error('Scan failed'); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="referrals-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">People &amp; HR · Benefits</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Employee Referrals</h1>
          <p className="text-sm text-slate-600 mt-1">
            Earn <strong>50% of one-month gross salary</strong> for every successful hire — paid after the candidate completes 6 months of service.
          </p>
        </div>
        <div className="flex gap-2">
          {isHRAdmin && (
            <button onClick={runProbationCron} className="btn-secondary inline-flex items-center gap-2" title="Manually run the probation-clearance scan" data-testid="run-probation-cron-btn">
              <RefreshCw size={14} /> Run Probation Scan
            </button>
          )}
          <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-referral-btn">
            <Plus size={15} /> New Referral
          </button>
        </div>
      </div>

      {policy && (
        <div className="mb-6 bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-sm p-5 flex items-start gap-3" data-testid="referral-policy-banner">
          <Users size={20} className="text-[#3b4872] shrink-0 mt-1" />
          <div className="text-xs text-slate-700">
            <div className="font-display font-bold text-sm text-[#0F172A] mb-1">Employee Referral Bonus Policy v1</div>
            <ul className="space-y-0.5">
              <li>· Bonus = <strong>{Math.round((policy.referral_bonus.percentage_of_gross || 0.5) * 100)}%</strong> of referee's 1-month gross salary.</li>
              <li>· Paid after referee completes <strong>{policy.referral_bonus.probation_months_required} months</strong> of continuous service.</li>
              <li>· <strong>{policy.referral_bonus.ineligible_departments.join(' / ')}</strong> departments cannot submit referrals.</li>
              <li>· Bonus is added as a payslip line in the next payroll cycle once probation is cleared.</li>
            </ul>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Referrer</th><th>Candidate</th><th>Position</th><th>Bonus</th><th>Eligible From</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {referrals.length === 0 && <tr><td colSpan="7" className="text-center p-8 text-sm text-slate-500">No referrals yet.</td></tr>}
            {referrals.map(r => (
              <tr key={r.id} data-testid={`referral-row-${r.id}`}>
                <td>
                  <div className="font-display font-semibold">{r.referrer_name}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{r.referrer_emp_code} · {r.referrer_department}</div>
                </td>
                <td>
                  <div className="font-semibold">{r.referee_name}</div>
                  {r.referee_email && <div className="text-[11px] text-slate-500">{r.referee_email}</div>}
                </td>
                <td className="text-xs">{r.referee_position || '—'}</td>
                <td className="font-display font-bold whitespace-nowrap">
                  {r.bonus_amount ? `₹${r.bonus_amount.toLocaleString('en-IN')}` : '—'}
                </td>
                <td className="text-[11px] font-mono">{r.eligible_from || '—'}</td>
                <td><span className={`inline-block text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_COLORS[r.status] || 'bg-slate-50 text-slate-700'}`}>{r.status}</span></td>
                <td className="text-right whitespace-nowrap">
                  {isHRAdmin && r.status === 'Submitted' && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setLinkModal(r); setLinkForm({ referee_employee_id: '', referee_gross_salary: '' }); }} className="px-2 py-1 text-[10px] font-display font-bold uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded-sm" data-testid={`link-referral-${r.id}`}>Link to Employee</button>
                      <button onClick={() => reject(r.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm" title="Reject"><XCircle size={14} /></button>
                    </div>
                  )}
                  {isHRAdmin && r.status === 'Probation Cleared' && (
                    <button onClick={() => markPaid(r.id)} className="px-2 py-1 text-[10px] font-display font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid={`mark-paid-${r.id}`}>
                      <IndianRupee size={11} className="inline -mt-0.5" /> Mark Paid
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New referral modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="referral-form-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Submit a Referral</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <label className="label">Referrer (Employee) *</label>
                <EmployeePicker value={form.referrer_employee_id} onChange={v => setForm(f => ({ ...f, referrer_employee_id: v }))} testid="referrer-picker" />
                <div className="text-[11px] text-slate-500 mt-1">HR / Admin department employees are ineligible to refer.</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Candidate Name *</label>
                  <input className="input" value={form.referee_name} onChange={(e) => setForm({ ...form, referee_name: e.target.value })} data-testid="referee-name" />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input type="email" className="input" value={form.referee_email} onChange={(e) => setForm({ ...form, referee_email: e.target.value })} />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" value={form.referee_phone} onChange={(e) => setForm({ ...form, referee_phone: e.target.value })} />
                </div>
                <div>
                  <label className="label">Position</label>
                  <input className="input" value={form.referee_position} onChange={(e) => setForm({ ...form, referee_position: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="label">Department (suggested)</label>
                  <input className="input" value={form.referee_department} onChange={(e) => setForm({ ...form, referee_department: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="submit-referral">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Link modal */}
      {linkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="link-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Link Referral · {linkModal.referee_name}</h2>
              <button onClick={() => setLinkModal(null)}><X size={20} /></button>
            </div>
            <form onSubmit={link} className="p-5 space-y-4">
              <div>
                <label className="label">Employee Record (after joining) *</label>
                <EmployeePicker value={linkForm.referee_employee_id} onChange={v => setLinkForm(f => ({ ...f, referee_employee_id: v }))} testid="link-emp-picker" />
              </div>
              <div>
                <label className="label">Monthly Gross Salary (₹) *</label>
                <input type="number" className="input" value={linkForm.referee_gross_salary} onChange={(e) => setLinkForm({ ...linkForm, referee_gross_salary: e.target.value })} data-testid="link-gross-salary" />
                {linkForm.referee_gross_salary && (
                  <div className="text-[11px] text-emerald-700 mt-1">
                    <CalendarDays size={11} className="inline -mt-0.5 mr-1" /> Bonus payable: <strong>₹{Math.round(parseFloat(linkForm.referee_gross_salary || 0) * 0.5).toLocaleString('en-IN')}</strong> after 6-month probation.
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setLinkModal(null)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="link-submit">Link & Schedule Bonus</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
